/**
 * Aspect — Transform mapping (server placement -> PlayCanvas entity)
 * --------------------------------------------------------------
 * PlayCanvas is already left-handed Y-up, so server [x, y, z] maps directly to
 * world position and `rotation_y` (degrees) maps directly to the entity's Y
 * Euler angle. The only subtlety is the anchor:
 *
 *   - placeholder boxes are centered at their origin, so a floor-anchored item
 *     must be lifted by half its (scaled) height.
 *   - glTF models are authored floor-anchored at their origin, so they sit at
 *     the placement position directly.
 *   - wall-hung items get a small outward nudge so the mesh back never sits
 *     coplanar with plaster (kills TV/painting z-fighting).
 *
 * Day 10 verifies coordinate round-trip and yaw-vs-wall alignment against this.
 */

import { isWallMounted, wallMountClearance } from "./furniture_placement.js";

const DEG = 1; // server already sends degrees
/** Extra meters to pull wall items off plaster (on top of stored pose). */
const WALL_ZFIGHT_NUDGE = 0.012;
/** Sit rugs above the wood slab so thin pile does not z-fight the floor. */
const RUG_FLOOR_CLEARANCE = 0.02;

export function isFloorRug(p) {
  return typeof p?.asset_id === "string" && /carpet_c$/i.test(p.asset_id);
}

export function floorRugLift(p) {
  if (!isFloorRug(p)) return 0;
  return RUG_FLOOR_CLEARANCE;
}

function biasRugMeshes(entity) {
  const visit = (node) => {
    for (const mi of node.render?.meshInstances ?? []) {
      const mat = mi?.material;
      if (!mat || mat._aspectRugBias) continue;
      const next = typeof mat.clone === "function" ? mat.clone() : mat;
      next.depthBias = -4;
      next._aspectRugBias = true;
      if (typeof next.update === "function") next.update();
      mi.material = next;
    }
    for (const child of node.children ?? []) visit(child);
  };
  visit(entity);
}

/** Keep yaw in [-180, 180) so lerps never take the long way around. */
export function normalizeYaw(deg) {
  let a = deg % 360;
  if (a >= 180) a -= 360;
  if (a < -180) a += 360;
  return a;
}

/**
 * Server staging uses y=0 as the active storey floor. Lift to world Y when the
 * room's active floor is above ground (DEFAULT_ACTIVE_FLOOR is 3).
 */
export function worldFloorY(y, floorY = 0) {
  return y === 0 && floorY !== 0 ? floorY : y;
}

/** Map a placement position into world space for rendering. */
export function worldPlacementPosition(position, floorY = 0) {
  const [x, y, z] = position ?? [0, 0, 0];
  return [x, worldFloorY(y, floorY), z];
}

/** Facing direction for a wall-hung item (into the room, away from the wall). */
function wallFacingXZ(rotYDeg) {
  const r = (rotYDeg * Math.PI) / 180;
  return { nx: Math.sin(r), nz: Math.cos(r) };
}

export function applyPlacementTransform(entity, p) {
  let [x, y, z] = p.position;
  const scale = p.scale ?? 1;
  const rotY = normalizeYaw(p.rotation_y ?? 0) * DEG;

  // Pull wall-hung meshes off the plaster so backs don't z-fight the wall.
  if (isWallMounted(p)) {
    const { nx, nz } = wallFacingXZ(rotY);
    const extra = Math.max(WALL_ZFIGHT_NUDGE, wallMountClearance(p.asset_id) * 0.35);
    x += nx * extra;
    z += nz * extra;
  }

  const rugLift = floorRugLift(p);

  if (entity._aspectKind === "placeholder" && p.footprint) {
    const w = p.footprint.w * scale;
    const h = p.footprint.h * scale;
    const d = p.footprint.d * scale;
    entity.setLocalScale(w, h, d);
    entity.setLocalPosition(x, y + h / 2 + rugLift, z);
  } else if (entity._aspectKind === "placeholder") {
    const fp = entity._aspectFootprint ?? { w: 1, h: 0.8, d: 1 };
    const w = fp.w * scale;
    const h = fp.h * scale;
    const d = fp.d * scale;
    entity.setLocalScale(w, h, d);
    entity.setLocalPosition(x, y + h / 2 + rugLift, z);
  } else {
    entity.setLocalScale(scale, scale, scale);
    entity.setLocalPosition(x, y + rugLift, z);
  }
  entity.setLocalEulerAngles(0, rotY, 0);
  if (rugLift) biasRugMeshes(entity);
}

/** Inverse helper: read an entity's transform back into a placement-ish object. */
export function readEntityTransform(entity, p) {
  const pos = entity.getLocalPosition();
  const scale = p?.scale ?? 1;
  let x = pos.x;
  let y = pos.y;
  let z = pos.z;
  if (entity._aspectKind === "placeholder" && p?.footprint) {
    y = pos.y - (p.footprint.h * scale) / 2;
  }
  y -= floorRugLift(p);
  const euler = entity.getLocalEulerAngles();
  const rotY = normalizeYaw(euler.y);
  // Undo the wall z-fight nudge so saved poses don't drift outward every edit.
  if (isWallMounted(p)) {
    const { nx, nz } = wallFacingXZ(rotY);
    const extra = Math.max(WALL_ZFIGHT_NUDGE, wallMountClearance(p.asset_id) * 0.35);
    x -= nx * extra;
    z -= nz * extra;
  }
  return { position: [x, y, z], rotation_y: rotY };
}
